import { useEffect } from 'react';
import { Grid, Typography, Box } from '@material-ui/core';
import { formatDate } from '@roc/client-portal-shared/utils';
import { makeStyles } from '@material-ui/core/styles';
import { amortizationOptions } from '@roc/feature-loans';
import { observer } from 'mobx-react';
import { formatCurrency, formatPercentage } from '@roc/ui/utils';
import { DEFAULT_FEE_RETAIL_SERVICING_SETUP, DEFAULT_FEE_LEGAL_REVIEW, DEFAULT_FEE_CORELOGIX } from '../../utils/constants';
import {
  getValueOrDefaultDash,
} from 'libs/feature-one-tool/src/quote/utils/utils';
import { ItemDisplay } from '../properties/borrowerLoanSubmission/components/itemDisplay'
import { TitleDivider } from '../../components/loanEconomics/components/titleDivider';
import { QuoteSummaryAccordion } from './components/quoteSummaryAccordion'

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    position: 'relative',
    maxWidth: '780px',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  itemDisplayValue: {
    fontSize: '20px',
    fontWeight: 500,
  },
  itemDisplayLabel: {
    fontSize: '12px',
    fontWeight: 400,
  },
  quoteSummaryIndexInformation: {
    fontWeight: 700,
    fontSize: 13,
    lineHeight: '143%',
    letterSpacing: '0.25px'
  },
  quoteSummaryIndexInformationData: {
    fontWeight: 400,
    fontSize: 15,
    lineHeight: '143%',
    letterSpacing: '0.25px'
  },
  informationDataContainer: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  captionText: {
    color: 'rgba(0, 0, 0, 0.38)',
    marginTop: '-10px',
    fontSize: 11,
    '& li': {
      marginLeft: 16
    }
  },
}));
export const QuoteSummary = observer(
  ({
    store
  }: any) => {
    const classes = useStyles();
    const { singlePropertyPropertyStore, loanEconomicsStore } = store;
    const { propertyFormStore } = singlePropertyPropertyStore;
    const { loanEconomicsFormStore } = loanEconomicsStore;
    const quoteData = loanEconomicsStore?.quote
    const propertyFormData = propertyFormStore?.form?.fields;
    const loanEconomicsFormData = loanEconomicsFormStore?.form?.fields
    const isOwnerOfTheProperty = propertyFormData?.propertyOwnership.value
    const amortizationType = amortizationOptions.find((option) => option.value === loanEconomicsFormData?.amortization?.value)?.label
    const propertyInformationRows = [
      { label: 'Property Type', value: getValueOrDefaultDash(propertyFormData?.propertyType?.value) },
      { label: 'Purchase Date', value: propertyFormData?.purchaseDate?.value ? formatDate(propertyFormData?.purchaseDate?.value, 'MM/dd/yyyy') : '-' },
      { label: 'Purchase Price', value: getValueOrDefaultDash(formatCurrency(propertyFormData?.purchasePrice?.value)) },
      { label: 'Completed Reno to Date', value: getValueOrDefaultDash(formatCurrency(propertyFormData?.renoBudget?.value)) },
      { label: 'Monthly Market Rent', value: getValueOrDefaultDash(formatCurrency(propertyFormData?.monthlyMarketRent?.value)) },
      { label: 'Estimated payoff amount', value: getValueOrDefaultDash(formatCurrency(propertyFormData?.payoffAmount?.value)) },
      { label: 'Estimated AS-IS Value', value: getValueOrDefaultDash(formatCurrency(propertyFormData?.currentMarketValue?.value)) },
      { label: 'Annual Taxes', value: getValueOrDefaultDash(formatCurrency(propertyFormData?.annualTaxes?.value)) },
      { label: 'Annual Insurance', value: getValueOrDefaultDash(formatCurrency(propertyFormData?.annualInsurance?.value)) },
      { label: 'Annual HOA', value: getValueOrDefaultDash(formatCurrency(propertyFormData?.annualHOA?.value)) },
    ];
    const loanTermsRows = [
      { label: 'Quote Type', value: isOwnerOfTheProperty ? 'Refinance' : 'Purchase' },
      { label: 'Amortization Type', value: getValueOrDefaultDash(amortizationType) },
      { label: 'Prepayment Penalty', value: getValueOrDefaultDash(loanEconomicsFormData?.prepenaltyPayment?.value) },
      { label: 'Pitia DSCR', value: getValueOrDefaultDash(quoteData?.dscr?.pitiaDscrInIOPeriod) },
      { label: `Today's Rate`, value: getValueOrDefaultDash(formatPercentage(quoteData?.rate ?? 0)) },
      { label: 'Monthly PI Payment', value: getValueOrDefaultDash(formatCurrency(quoteData?.pricerLoanTerms?.monthlyPaymentWithoutExpenses ?? 0)) },
      { label: 'Monthly Pitia Payment', value: getValueOrDefaultDash(formatCurrency(quoteData?.pricerLoanTerms?.monthlyPayment ?? 0)) },
      { label: 'Post Loan Approval Rate Lock', value: '-' },
    ];
    const pointsAndFeesRows = [
      { label: 'Origination Points', value: formatPercentage(quoteData?.pointsAndFeesPercent + quoteData?.addOnPoints) },
      { label: 'Legal Review Fee', value: formatCurrency(DEFAULT_FEE_LEGAL_REVIEW) },
      { label: `Servicing Setup Fee`, value: formatCurrency(DEFAULT_FEE_RETAIL_SERVICING_SETUP) },
      { label: `Corelogix Fee`, value: formatCurrency(DEFAULT_FEE_CORELOGIX) },
      { label: 'Admin Fee', value: formatCurrency(loanEconomicsStore?.borrowerSubmissionFees?.adminFees) },
      { label: 'Commitment Fee', value: formatCurrency(loanEconomicsStore?.borrowerSubmissionFees?.commitmentFees) },
      { label: 'Processing Fee', value: formatCurrency(loanEconomicsStore?.borrowerSubmissionFees?.processingFees) },
      { label: 'Underwriting Fee', value: formatCurrency(loanEconomicsStore?.borrowerSubmissionFees?.underwriterFees) },
      { label: 'Buydown Fee', value: formatCurrency(loanEconomicsStore?.borrowerSubmissionFees?.buydownFees) },
    ];
    const fullAddress = singlePropertyPropertyStore.buildFullAddress();
    useEffect(() => {
      store?.getAttorneys(propertyFormData?.state.value, "term");
    }, []);
    return (
      <Grid
        container
        spacing={2}
        className={classes.container}
      >
        <Grid item xs={12}>
          <Typography className={classes.quoteSummaryIndexInformation}>
            Index and Rate Lock Information:
          </Typography>
          <Box className={classes.informationDataContainer}>
            <Typography className={classes.quoteSummaryIndexInformationData}>
              USD 3M Libor US Treasury Rates as of <strong>{formatDate(new Date, 'MM/dd/yyyy')}</strong>
            </Typography>
            <Typography className={classes.quoteSummaryIndexInformationData}>
              5 Yr US Treasury: <strong>{formatPercentage(quoteData?.fiveYrTsrySwap ?? 0)}</strong>
            </Typography>
            <Typography className={classes.quoteSummaryIndexInformationData}>
              10 Yr US Treasury: <strong>{formatPercentage(quoteData?.tenYrTsrySwap ?? 0)}</strong>
            </Typography>
          </Box>
        </Grid>
        <TitleDivider title="" />
        <Grid item xs={8}>
          <ItemDisplay label={<Box className={classes.itemDisplayLabel}>PROPERTY ADDRESS</Box>} value={<Box className={classes.itemDisplayValue}>{getValueOrDefaultDash(fullAddress)}</Box>}></ItemDisplay>
        </Grid>
        <Grid item xs={4}>
          <ItemDisplay label={<Box className={classes.itemDisplayLabel}>LOAN AMOUNT</Box>} value={<Box className={classes.itemDisplayValue}>{formatCurrency(quoteData?.pricerLoanTerms?.amount)}</Box>}></ItemDisplay>
        </Grid>
        {/*<Grid item xs={4}>
          <ItemDisplay label={<Box className={classes.itemDisplayLabel}>ESTIMATED CASH TO CLOSE</Box>} value={<Box className={classes.itemDisplayValue}>{formatCurrency(quoteData?.cashToClose ?? 0) ?? '-'}</Box>}></ItemDisplay>
        </Grid>*/}
        <Grid item xs={4}>
          <ItemDisplay label={<Box className={classes.itemDisplayLabel}>LOAN TO VALUE</Box>} value={<Box className={classes.itemDisplayValue}>{getValueOrDefaultDash(formatPercentage(loanEconomicsStore?.selectedLtv))}</Box>}></ItemDisplay>
        </Grid>
        <Grid item xs={4}>
          <ItemDisplay label={<Box className={classes.itemDisplayLabel}>RATE TYPE</Box>} value={<Box className={classes.itemDisplayValue}>{loanEconomicsFormData?.rateType?.value}</Box>}></ItemDisplay>
        </Grid>
        <Grid item xs={12}>
          <QuoteSummaryAccordion store={store} title="Property information" data={propertyInformationRows} />
        </Grid>
        <Grid item xs={12}>
          <QuoteSummaryAccordion store={store} title="Loan Terms" data={loanTermsRows} />
        </Grid>
        <Grid item xs={12}>
          <QuoteSummaryAccordion store={store} title="Points and Fees" data={pointsAndFeesRows} />
        </Grid>
        <Grid item xs={12}>
          <ul className={classes.captionText}>
            <strong>Please refer to Rental Loan Rates (FAQs for more information)</strong>
            <li>Gross rates are determined at the time of credit approval.</li>
          </ul>
        </Grid>
      </Grid>
    );
  }
);
